import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { apiDomain } from 'configs/configs';
import { logout } from 'helpers/helpers';
import {
  Checkbox,
  Input,
  Form,
  Typography,
  Button,
  notification,
  Row,
  Space,
} from 'antd';
import Table, { type ColumnsType } from 'components/table/table';
import MobileInnerWrapper from 'layouts/mobileInnerWrapper/mobileInnerWrapper';
import useScheduleStore from 'stores/seniorMaster/schedule';
import useUserStore from 'stores/user';
import { MasterObjectType } from 'pages/seniorMaster/schedule/types';
import styles from 'pages/seniorMaster/schedule/schedule.module.scss';

const { Text, Title } = Typography;

type ComponentProps = {};

const Page: React.FC<ComponentProps> = () => {
  const {
    daysCount,
    list,
    setList,
    isPostInProcess,
    setIsPostInProcess,
    isLoadingInProcess,
    setIsLoadingInProcess,
    fromDate,
    setFromDate,
  } = useScheduleStore((store) => store);

  const { user } = useUserStore((store) => store);
  const listRef = useRef(list);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: 'Ошибка',
        description: message,
        placement: 'topRight',
        duration: 3,
      });
    },
    []
  );

  //  Success message
  const openNotificationWithSuccess = useCallback(
    ({ message }: { message: string }) => {
      notification.success({
        message: 'Сохранено',
        description: message,
        placement: 'topRight',
        duration: 3,
      });
    },
    []
  );

  const handleEditObjectScheduleValue = useCallback(
    ({
      objectId,
      masterId,
      date,
      value,
      isChecked,
    }: {
      objectId: number;
      masterId: number;
      date: string;
      value: string;
      isChecked: boolean;
    }) => {
      const userId = user.userData.id;

      if (!userId) return;

      setList((list) => {
        const listData = list.data.map((dataItem) => {
          if (dataItem.id !== objectId) return dataItem;

          const schedule = dataItem.schedule;

          const foundMasterSchedule = schedule[masterId];

          const foundScheduleItem = foundMasterSchedule
            ? foundMasterSchedule.find((scheduleItem) => {
                if (scheduleItem.object_id !== objectId) return false;
                if (scheduleItem.date !== date) return false;

                return true;
              })
            : undefined;

          const valueCleaned = !value
            ? ''
            : value
                .replace(/[^\d.,]/g, '')
                .replace(/,/g, '.')
                .replace(/^(\d*(\.\d{0,2})?).*$/, '$1');

          if (foundScheduleItem) {
            foundScheduleItem.value = valueCleaned;
            foundScheduleItem.isNotSaved = true;
          }

          if (!foundScheduleItem) {
            if (!schedule[masterId]) schedule[masterId] = [];

            schedule[masterId].push({
              isNotSaved: true,
              object_id: objectId,
              master_id: masterId,
              date: date,
              value: valueCleaned,
              confirmed_by_senior_master: isChecked,
              confirmed_by_senior_master_id: isChecked ? userId : null,
              calculated_sum: '0',
            });
          }

          return { ...dataItem, schedule: schedule };
        });

        return {
          ...list,
          data: listData,
        };
      });
    },
    [user, setList]
  );

  const handleEditObjectScheduleConfirmed = useCallback(
    ({
      objectId,
      masterId,
      date,
      isChecked,
      value,
    }: {
      objectId: number;
      masterId: number;
      date: string;
      isChecked: boolean;
      value: string;
    }) => {
      const userId = user.userData.id;

      if (!userId) return;

      setList((list) => {
        const listData = list.data.map((dataItem) => {
          if (dataItem.id !== objectId) return dataItem;

          const schedule = dataItem.schedule;

          const foundMasterSchedule = schedule[masterId];

          const foundScheduleItem = foundMasterSchedule
            ? foundMasterSchedule.find((scheduleItem) => {
                if (scheduleItem.object_id !== objectId) return false;
                if (scheduleItem.date !== date) return false;

                return true;
              })
            : undefined;

          if (foundScheduleItem) {
            foundScheduleItem.confirmed_by_senior_master = isChecked;
            foundScheduleItem.isNotSaved = true;
          }

          if (!foundScheduleItem) {
            if (!schedule[masterId]) schedule[masterId] = [];

            schedule[masterId].push({
              isNotSaved: true,
              object_id: objectId,
              master_id: masterId,
              date: date,
              value: value,
              confirmed_by_senior_master: isChecked,
              confirmed_by_senior_master_id: isChecked ? userId : null,
              calculated_sum: '0',
            });
          }

          return { ...dataItem, schedule: schedule };
        });

        return {
          ...list,
          data: listData,
        };
      });
    },
    [user, setList]
  );

  const scheduleData = useMemo(() => {
    if (!list.isLoaded) return [];

    return list.data.filter((object) => {
      if (object.status !== 'in_progress') return false;

      const seniorMaster = object.senior_masters
        ? object.senior_masters.find(
            (senior_master) => senior_master === user.userData.id
          )
        : undefined;

      if (!seniorMaster) return false;

      return true;
    });
  }, [list, user]);

  const daysOfWeek = useMemo(() => {
    return ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
  }, []);

  const scheduleColumns = useCallback(
    (objectId: number) => {
      const result: ColumnsType = [
        {
          title: 'Объект',
          key: 'objectAddress',
          render: (text: any, untypedRecord: unknown) => {
            const record = untypedRecord as {
              objectId: number;
              schedule: MasterObjectType['schedule'][string];
              master:
                | undefined
                | NonNullable<MasterObjectType['masters']>[number];
            };

            if (!record.master) return null;

            return <div>{record.master.name}</div>;
          },
        },
      ];

      const currentObjectFromDate = fromDate[objectId]
        ? fromDate[objectId]
        : new Date(new Date().setDate(new Date().getDate() - (daysCount - 1)))
            .toISOString()
            .split('T')[0];

      const currentDate = new Date(currentObjectFromDate);

      let previousMonth = currentDate.getMonth();
      let children: ColumnsType = [];

      for (let daysToAdd = 0; daysToAdd <= daysCount - 1; daysToAdd++) {
        const futureDate = new Date(currentDate);
        futureDate.setDate(currentDate.getDate() + daysToAdd);

        const dateString = futureDate.toISOString().split('T')[0];
        const day = futureDate.getDate();
        const dayOfWeek = daysOfWeek[futureDate.getDay()];

        // Проверка на смену месяца до вывода даты
        const checkDate = new Date(futureDate);
        checkDate.setDate(futureDate.getDate() + 1);

        // Записываем дату до смены месяца
        children.push({
          title: (
            <>
              {dayOfWeek}
              <br />
              {day}
            </>
          ),
          key: dateString,
          align: 'right',
          render: (text: any, untypedRecord: unknown) => {
            const record = untypedRecord as {
              objectId: number;
              schedule: MasterObjectType['schedule'][string];
              master:
                | undefined
                | NonNullable<MasterObjectType['masters']>[number];
            };

            const master = record.master;

            if (!master) return null;

            const scheduleItem = record.schedule.find((scheduleItem) => {
              if (scheduleItem.date !== dateString) return false;

              return true;
            });

            const value = scheduleItem ? scheduleItem.value : '';
            const isChecked = scheduleItem
              ? !!scheduleItem?.confirmed_by_senior_master
              : false;
            const hourlyRate = master?.hourly_rate ? master.hourly_rate : 0;

            const total = (value ? parseFloat(value) : 0) * hourlyRate;
            const isConfirmed = !!scheduleItem?.confirmed_by_senior_master;

            const isConfirmedAndSaved =
              !!scheduleItem?.confirmed_by_senior_master &&
              !scheduleItem?.isNotSaved;

            const isFuture =
              new Date(dateString).getTime() >
              new Date().setHours(23, 59, 59, 999);

            return (
              <>
                <Input
                  size="small"
                  name={`objects_values[${master.id}][${dateString}]`}
                  onChange={(event) =>
                    handleEditObjectScheduleValue({
                      objectId: record.objectId,
                      masterId: master.id,
                      date: dateString,
                      value: event.target.value,
                      isChecked: isChecked,
                    })
                  }
                  value={value}
                  disabled={
                    isPostInProcess[record.objectId] ||
                    isConfirmedAndSaved ||
                    isFuture
                  }
                  className={styles.hours}
                />
                <div className={styles.isConfirmed}>
                  <Checkbox
                    name={`objects_confirmed[${master.id}][${dateString}]`}
                    onChange={(event) =>
                      handleEditObjectScheduleConfirmed({
                        objectId: record.objectId,
                        masterId: master.id,
                        date: dateString,
                        isChecked: event.target.checked,
                        value: value,
                      })
                    }
                    checked={isConfirmed}
                    disabled={
                      isPostInProcess[record.objectId] ||
                      isConfirmedAndSaved ||
                      isFuture
                    }
                    className={styles.checkbox}
                  />
                </div>
                <div className={styles.total}>{total.toFixed(2)}</div>
              </>
            );
          },
        });

        // Проверка на смену месяца
        if (
          checkDate.getMonth() !== previousMonth ||
          daysToAdd === daysCount - 1
        ) {
          result.push({
            title: `${futureDate.toLocaleString('default', {
              month: 'long',
              year: 'numeric',
            })}`,
            key: 'scheduleMonth',
            children,
          });

          children = [];
          previousMonth = checkDate.getMonth();
        }
      }

      return result;
    },
    [
      daysCount,
      fromDate,
      daysOfWeek,
      isPostInProcess,
      handleEditObjectScheduleValue,
      handleEditObjectScheduleConfirmed,
    ]
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Get all objects
  const getList = useCallback(
    async (
      isRefresh?: boolean,
      fromDate: string = new Date(
        new Date().setDate(new Date().getDate() - (daysCount - 1))
      )
        .toISOString()
        .split('T')[0],
      objectId?: number
    ) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/objects${
            objectId ? `/${objectId}` : ``
          }?daysCount=${daysCount}&fromDate=${fromDate}`;
          const options: RequestInit = {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records && !objectId) throw new Error();

          setList((list) => {
            if (!objectId)
              return {
                isLoaded: true,
                data: data.records,
              };

            const resultData = [...list.data].map((dataElement) => {
              if (dataElement.id === data.id) return data;

              return dataElement;
            });

            return {
              isLoaded: true,
              data: resultData,
            };
          });
        } catch (error) {
          logout();
        }
      };

      if (listRef.current.isLoaded && !isRefresh) return;

      if (objectId)
        setIsLoadingInProcess((isLoadingInProcess) => ({
          ...isLoadingInProcess,
          [objectId]: true,
        }));

      await fetchData();

      if (objectId)
        setIsLoadingInProcess((isLoadingInProcess) => ({
          ...isLoadingInProcess,
          [objectId]: false,
        }));
    },
    [setList, setIsLoadingInProcess, daysCount]
  );

  //  When started, get data
  useEffect(() => {
    getList();

    return () => {
      //  When leave page reset all
      setList({ isLoaded: false, data: [] });
    };
  }, [getList, setList]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(
    async (objectId: number) => {
      if (isPostInProcess[objectId]) return;
      if (!scheduleData?.length) return;

      const fetchData = async () => {
        setIsPostInProcess((isPostInProcess) => {
          return { ...isPostInProcess, [objectId]: true };
        });

        const objectData = scheduleData.find(
          (object) => object.id === objectId
        );

        if (!objectData) return;

        const url = `${apiDomain}/api/records/objects/${objectData.id}`;

        const options: RequestInit = {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            schedule: Object.values(objectData.schedule).flat(),
          }),
          credentials: 'include',
        };

        try {
          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          openNotificationWithSuccess({
            message: `Расписание объекта ${
              objectData.address ? objectData.address : objectData.name
            } успешно сохранено`,
          });
        } catch (error) {
          openNotificationWithError({
            message: `Не удалось сохранить расписание объекта ${
              objectData.address ? objectData.address : objectData.name
            }`,
          });
        }

        const currentObjectFromDate = fromDate[objectId]
          ? fromDate[objectId]
          : new Date(new Date().setDate(new Date().getDate() - (daysCount - 1)))
              .toISOString()
              .split('T')[0];

        await getList(true, currentObjectFromDate, objectId);

        setIsPostInProcess((isPostInProcess) => {
          return { ...isPostInProcess, [objectId]: false };
        });
      };

      fetchData();
    },
    [
      isPostInProcess,
      scheduleData,
      fromDate,
      daysCount,
      getList,
      openNotificationWithError,
      openNotificationWithSuccess,
      setIsPostInProcess,
    ]
  );

  const handleShowPreviousDates = useCallback(
    (objectId: number) => {
      setFromDate((fromDate) => {
        const currentObjectFromDate = fromDate[objectId]
          ? fromDate[objectId]
          : new Date(new Date().setDate(new Date().getDate() - (daysCount - 1)))
              .toISOString()
              .split('T')[0];

        const currentFromDate = new Date(currentObjectFromDate);
        currentFromDate.setDate(currentFromDate.getDate() - daysCount);

        const previousStartDate = currentFromDate.toISOString().split('T')[0];

        getList(true, previousStartDate, objectId);

        return { ...fromDate, [objectId]: previousStartDate };
      });
    },
    [getList, setFromDate, daysCount]
  );

  const handleShowNextDates = useCallback(
    (objectId: number) => {
      setFromDate((fromDate) => {
        const currentObjectFromDate = fromDate[objectId]
          ? fromDate[objectId]
          : new Date(new Date().setDate(new Date().getDate() - (daysCount - 1)))
              .toISOString()
              .split('T')[0];

        const currentFromDate = new Date(currentObjectFromDate);
        currentFromDate.setDate(currentFromDate.getDate() + daysCount);

        const nextStartDate = currentFromDate.toISOString().split('T')[0];

        getList(true, nextStartDate, objectId);

        return { ...fromDate, [objectId]: nextStartDate };
      });
    },
    [getList, setFromDate, daysCount]
  );

  const isLoaded = useMemo(() => {
    const result: { [objectId: number]: boolean } = {};

    if (!list.data?.length) return result;

    for (const object of list.data) {
      result[object.id] =
        !list.isLoaded || isLoadingInProcess[object.id] || false;
    }

    return result;
  }, [list, isLoadingInProcess]);

  return (
    <MobileInnerWrapper>
      <Title level={3} className={styles.title}>
        Редактирование графика
      </Title>
      <div className={styles.contentBlock}>
        {!!(!!list.isLoaded && !scheduleData?.length) && (
          <Text>
            На данный момент у вас нет активных объектов, где вы назначены
            старшим мастером.
          </Text>
        )}
        {!!(!list.isLoaded || !!scheduleData?.length) &&
          list.data.map((object) => {
            if (!object.masters?.length) return null;

            const dataSource = object.masters.map((master) => {
              return {
                masterId: master.id,
                objectId: object.id,
                schedule: object.schedule[master.id]
                  ? object.schedule[master.id]
                  : [],
                master: master,
              };
            });

            return (
              <div className={styles.contentBlock} key={object.id}>
                <Row
                  justify="space-between"
                  align="middle"
                  className={styles.subTitle}
                >
                  <Title level={4}>
                    График работы объекта{' '}
                    {object.address ? object.address : object.name}
                  </Title>
                  <Space>
                    <Button
                      onClick={() => handleShowPreviousDates(object.id)}
                      disabled={!!isLoaded?.[object.id]}
                    >
                      Показать предыдущие даты
                    </Button>
                    <Button
                      onClick={() => handleShowNextDates(object.id)}
                      disabled={!!isLoaded?.[object.id]}
                    >
                      Показать следующие даты
                    </Button>
                  </Space>
                </Row>
                <Form
                  layout={'vertical'}
                  onFinish={() => handleFinish(object.id)}
                >
                  <Table
                    size="small"
                    dataSource={dataSource}
                    columns={scheduleColumns(object.id)}
                    rowKey="masterId"
                    pagination={false}
                    loading={!!isLoaded?.[object.id]}
                    styles={styles}
                    className={styles.table}
                  />
                  <Button
                    type="primary"
                    size="large"
                    className={styles.button}
                    disabled={!!isLoaded?.[object.id]}
                    htmlType="submit"
                    loading={isPostInProcess[object.id]}
                  >
                    Сохранить
                  </Button>
                </Form>
              </div>
            );
          })}
      </div>
    </MobileInnerWrapper>
  );
};

export default Page;
